import { translate } from 'shared/lib/i18n.tsx';
import { defaultRemoteViewLayout } from 'shared/lib/tv-game/nativeLayout.ts';
import { getRemoteCalibStandZoneMetrics } from 'shared/lib/tv-game/remoteCalibStandZone.ts';

import { drawRemoteCalibrationStandZone } from '../infrastructure/canvas/room/calibrationShared.ts';
import { wrapCanvasText } from '../infrastructure/canvas/room/canvasHelpers.ts';

import { drawFillProgress } from './BirdFlightDisplayEngine.ts';
import { parseLaserDodgeFrame, type LaserDodgeFrame } from './laserDodgeFrame.ts';

/**
 * Laser Dodge на ТВ (GEN-18724). Игра целиком на телефоне: он ведёт раунды (предупреждение → огонь → итог), считает
 * касание зон, жизни и счёт, ставит паузу. ТВ — только экран: телефон шлёт кадры
 * `{type: 'LaserDodgeData', gameState, detectionProgress, phase, phaseProgress, round, lives, score, zones, joints,
 * bones, …}` ~30 раз/с, ТВ рисует зоны, скелет игрока и счёт. Координаты — доли экрана телефона (он в ландшафте),
 * поэтому ложатся на весь экран ТВ как есть.
 *
 * Пауза, возобновление, рестарт и итог идут полем `gameState` кадров (канал без гарантий доставки: телефон повторяет
 * кадры состояния; повторы здесь безвредны, действие выполняется только при смене фазы).
 */
export type LaserDodgePhaseOnTv = 'lobby' | 'calibration' | 'playing' | 'paused' | 'gameover';

const Z_INDEX = '1';

const BACKDROP_TOP = '#070b1f';
const BACKDROP_BOTTOM = '#10132e';
const ZONE_RGB = '255, 40, 64';
/** Сетка зон телефона — 8×4; на ТВ она еле видна, чтобы глаз цеплялся за границы. */
const GRID_COLUMNS = 8;
const GRID_ROWS = 4;

function drawBackdrop(ctx: CanvasRenderingContext2D, W: number, H: number): void {
  const backdrop = ctx.createLinearGradient(0, 0, 0, H);
  backdrop.addColorStop(0, BACKDROP_TOP);
  backdrop.addColorStop(1, BACKDROP_BOTTOM);
  ctx.fillStyle = backdrop;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let c = 1; c < GRID_COLUMNS; c += 1) {
    ctx.moveTo((W * c) / GRID_COLUMNS, 0);
    ctx.lineTo((W * c) / GRID_COLUMNS, H);
  }
  for (let r = 1; r < GRID_ROWS; r += 1) {
    ctx.moveTo(0, (H * r) / GRID_ROWS);
    ctx.lineTo(W, (H * r) / GRID_ROWS);
  }
  ctx.stroke();
}

function drawZones(ctx: CanvasRenderingContext2D, W: number, H: number, frame: LaserDodgeFrame): void {
  if (frame.zones.length === 0) return;
  const { phase, phaseProgress } = frame;
  let alpha = 0;
  if (phase === 'warning') alpha = 0.18 + 0.22 * phaseProgress;
  else if (phase === 'firing') alpha = 0.85;
  else if (phase === 'result') alpha = 0.4 * (1 - phaseProgress);
  if (alpha <= 0) return;

  ctx.save();
  if (phase === 'firing') {
    ctx.shadowColor = `rgba(${ZONE_RGB}, 0.9)`;
    ctx.shadowBlur = H * 0.03;
  }
  ctx.fillStyle = `rgba(${ZONE_RGB}, ${alpha})`;
  ctx.strokeStyle = `rgba(${ZONE_RGB}, ${Math.min(1, alpha + 0.3)})`;
  ctx.lineWidth = Math.max(2, H * 0.004);
  frame.zones.forEach((zone) => {
    const x = zone.left * W;
    const y = zone.top * H;
    const w = (zone.right - zone.left) * W;
    const h = (zone.bottom - zone.top) * H;
    ctx.fillRect(x, y, w, h);
    if (phase === 'warning') ctx.setLineDash([H * 0.02, H * 0.015]);
    ctx.strokeRect(x, y, w, h);
  });
  ctx.restore();
}

function drawOverlay(ctx: CanvasRenderingContext2D, W: number, H: number, text: string): void {
  ctx.save();
  ctx.fillStyle = 'rgba(8, 6, 24, 0.7)';
  ctx.fillRect(0, 0, W, H);
  const px = Math.round(Math.min(H * 0.07, 80));
  ctx.font = `800 ${px}px -apple-system, system-ui, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#fff';
  ctx.fillText(text, W / 2, H / 2);
  ctx.restore();
}

export class LaserDodgeDisplayEngine {
  private phase: LaserDodgePhaseOnTv = 'lobby';

  private host: HTMLDivElement | null = null;

  private canvas: HTMLCanvasElement | null = null;

  private raf = 0;

  /** Заполнение обратного отсчёта на телефоне, 0…1. */
  private detectionProgress = 0;

  /** Последний кадр `STARTED`: из него рисуем игру, в том числе на паузе и под итогом. */
  private view: LaserDodgeFrame | null = null;

  /** Итог игры из кадра `GAME_OVER`. */
  private final: { round: number; score: number } | null = null;

  /** После последнего (ре)старта приходили кадры `STARTED`. */
  private sawStarted = false;

  getPhase(): LaserDodgePhaseOnTv {
    return this.phase;
  }

  /** `select-game` и рестарт с телефона: отсчёт «встань в силуэт» на телефоне, ТВ рисует рамку по его прогрессу. */
  startCalibration(): void {
    this.ensureHost();
    this.sawStarted = false;
    this.detectionProgress = 0;
    this.view = null;
    this.final = null;
    this.setPhase('calibration');
  }

  /** `start-game` с телефона: отсчёт пройден, начинаем игру. */
  startGame(): void {
    this.ensureHost();
    this.sawStarted = false;
    this.view = null;
    this.final = null;
    this.setPhase('playing');
  }

  onGameFrame(rawFrame: unknown): void {
    const frame = parseLaserDodgeFrame(rawFrame);
    if (!frame || this.phase === 'lobby') return;

    switch (frame.gameState) {
    case 'PREPARING':
      // Рестарт с телефона (после паузы или итога) — снова отсчёт. Запоздавший кадр подготовки сразу после
      // `start-game`, до первого кадра игры, рестартом не считаем.
      if (this.phase === 'playing' && !this.sawStarted) return;
      if (this.phase !== 'calibration') this.startCalibration();
      if (frame.detectionProgress !== null) this.detectionProgress = frame.detectionProgress;
      break;
    case 'PAUSED':
      if (this.phase === 'playing') this.setPhase('paused');
      break;
    case 'STARTED':
      if (this.phase !== 'playing' && this.phase !== 'paused') return;
      this.sawStarted = true;
      this.view = frame;
      if (this.phase === 'paused') this.setPhase('playing');
      break;
    case 'GAME_OVER':
      if (this.phase !== 'playing' && this.phase !== 'paused') return;
      this.final = {
        round: frame.round ?? this.view?.round ?? 0,
        score: frame.score ?? this.view?.score ?? 0,
      };
      this.setPhase('gameover');
      break;
    default:
      break;
    }
  }

  reset(): void {
    this.setPhase('lobby');
    this.host?.remove();
    this.host = null;
    this.canvas = null;
    this.view = null;
    this.final = null;
  }

  private setPhase(phase: LaserDodgePhaseOnTv): void {
    this.phase = phase;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (phase === 'lobby') return;
    const tick = (): void => {
      if (this.phase === 'lobby') return;
      this.draw();
      this.raf = requestAnimationFrame(tick);
    };
    tick();
  }

  private ensureHost(): void {
    if (this.host) return;
    const host = document.createElement('div');
    host.style.cssText = `position:fixed;inset:0;z-index:${Z_INDEX};background:${BACKDROP_TOP};`;
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;';
    host.appendChild(canvas);
    document.body.appendChild(host);
    this.host = host;
    this.canvas = canvas;
  }

  // ─── Отрисовка ─────────────────────────────────────────────────────────────

  private draw(): void {
    const { canvas } = this;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const W = window.innerWidth;
    const H = window.innerHeight;
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;

    if (this.phase === 'calibration') {
      this.drawCalibration(ctx, W, H);
      return;
    }
    drawBackdrop(ctx, W, H);
    const frame = this.view;
    if (frame) {
      drawZones(ctx, W, H, frame);
      this.drawBody(ctx, W, H, frame);
      this.drawHud(ctx, W, H, frame);
      this.drawRoundMessage(ctx, W, H, frame);
    }
    if (this.phase === 'paused') drawOverlay(ctx, W, H, translate('game.pause'));
    if (this.phase === 'gameover') {
      drawOverlay(ctx, W, H, translate('game.laser_dodge_over', {
        score: this.final?.score ?? 0,
        round: this.final?.round ?? 0,
      }));
    }
  }

  private drawBody(ctx: CanvasRenderingContext2D, W: number, H: number, frame: LaserDodgeFrame): void {
    if (this.phase === 'gameover') return;
    ctx.save();
    ctx.lineCap = 'round';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
    ctx.lineWidth = Math.max(4, H * 0.009);
    ctx.beginPath();
    frame.bones.forEach(([x1, y1, x2, y2]) => {
      ctx.moveTo(x1 * W, y1 * H);
      ctx.lineTo(x2 * W, y2 * H);
    });
    ctx.stroke();
    ctx.fillStyle = '#7fe8ff';
    const radius = Math.max(5, H * 0.012);
    frame.joints.forEach(({ x, y }) => {
      ctx.beginPath();
      ctx.arc(x * W, y * H, radius, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  private drawHud(ctx: CanvasRenderingContext2D, W: number, H: number, frame: LaserDodgeFrame): void {
    const px = Math.round(Math.min(H * 0.05, 56));
    const pad = px * 0.8;
    ctx.save();
    ctx.font = `800 ${px}px -apple-system, system-ui, Arial, sans-serif`;
    ctx.textBaseline = 'top';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = px * 0.3;
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'left';
    const score = this.phase === 'gameover' ? this.final?.score ?? 0 : frame.score ?? 0;
    ctx.fillText(`${translate('game.score')} ${score}`, pad, pad);
    ctx.textAlign = 'center';
    ctx.fillText(translate('game.laser_dodge_round', { n: frame.round ?? 0 }), W / 2, pad);
    ctx.textAlign = 'right';
    const lives = this.phase === 'gameover' ? 0 : frame.lives ?? 0;
    const total = Math.max(3, lives);
    for (let i = 0; i < total; i += 1) {
      ctx.fillStyle = i < lives ? '#ff4d6d' : 'rgba(255, 255, 255, 0.25)';
      ctx.fillText('♥', W - pad - i * px * 1.1, pad);
    }
    ctx.restore();
  }

  private drawRoundMessage(ctx: CanvasRenderingContext2D, W: number, H: number, frame: LaserDodgeFrame): void {
    if (this.phase !== 'playing') return;
    let text: string | null = null;
    let color = '#ffe566';
    if (frame.phase === 'warning') {
      text = translate('game.laser_dodge_warning');
    } else if (frame.phase === 'result') {
      if (frame.roundPassed) {
        text = translate('game.laser_dodge_dodged', { n: frame.roundPoints });
        color = '#4cd964';
      } else {
        text = translate(frame.bodySeen ? 'game.laser_dodge_hit' : 'game.laser_dodge_not_seen');
        color = '#ff6b81';
      }
    }
    if (!text) return;
    const px = Math.round(Math.min(H * 0.07, 80));
    ctx.save();
    ctx.font = `800 ${px}px -apple-system, system-ui, Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
    ctx.shadowBlur = px * 0.4;
    ctx.fillStyle = color;
    ctx.fillText(text, W / 2, H * 0.9);
    ctx.restore();
  }

  /** Экран «встаньте в зону», как у Bird Flight: полный силуэт — по телу бьют зоны по всему экрану. */
  private drawCalibration(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    const layout = defaultRemoteViewLayout(W, H);
    const backdrop = ctx.createLinearGradient(0, 0, 0, H);
    backdrop.addColorStop(0, '#040c26');
    backdrop.addColorStop(0.6, '#07143a');
    backdrop.addColorStop(1, '#04081c');
    ctx.fillStyle = backdrop;
    ctx.fillRect(0, 0, W, H);
    drawRemoteCalibrationStandZone(ctx, layout, 'full');

    const {
      cx, boxX, boxY, boxW, boxH, borderW,
    } = getRemoteCalibStandZoneMetrics(layout, 'full');
    drawFillProgress(ctx, {
      x: boxX, y: boxY, w: boxW, h: boxH, lineWidth: borderW * 1.6, progress: this.detectionProgress,
    });
    const px = Math.round(Math.min(layout.gameHeight * 0.034, boxW / 14, 46));
    const padX = boxW * 0.05;
    const sw = boxW - padX * 2;
    const lineGap = px * 1.45;
    ctx.save();
    ctx.font = `600 ${px}px Arial, sans-serif`;
    const lines = wrapCanvasText(ctx, translate('calib.hint.full_body'), sw);
    const padV = px * 0.55;
    const sy = boxY + borderW * 2.2 + Math.max(10, layout.gameHeight * 0.015);
    ctx.fillStyle = 'rgba(0,0,0,0.58)';
    ctx.beginPath();
    ctx.rect(boxX + padX, sy, sw, lines.length * lineGap + padV * 2);
    ctx.fill();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffe566';
    lines.forEach((line, i) => ctx.fillText(line, cx, sy + padV + lineGap * (i + 0.5)));
    ctx.restore();
  }
}
